import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import {
  CalendarIcon, Search, Filter, ChevronLeft, ChevronRight, Eye, UserCog,
  MapPin, CreditCard, Clock, RefreshCw, Loader2, LayoutGrid, TableIcon,
  CheckCircle2, AlertTriangle, FileText, ArrowLeft, XCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import RecordActions from '../../components/RecordActions';
import { AdminSubNav, SUBNAV_SCHEDULE } from '../../components/admin/AdminSubNav';
import MultiGuruSelect from '../../components/admin/MultiGuruSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  'Booked': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Confirmed': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'In Progress': 'bg-blue-100 text-blue-800 border-blue-200',
  'Service Finished': 'bg-purple-100 text-purple-800 border-purple-200',
  'Admin Confirmed': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'Completed': 'bg-green-100 text-green-800 border-green-200',
  'Canceled': 'bg-red-100 text-red-800 border-red-200',
};

const INVOICE_COLORS = {
  'Draft': 'bg-zinc-100 text-zinc-700',
  'Ready for Charge': 'bg-orange-100 text-orange-700',
  'Paid': 'bg-green-100 text-green-700',
  'Payment Failed': 'bg-red-100 text-red-700',
  'Void': 'bg-zinc-100 text-zinc-500',
};

const AdminAppointmentsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { back } = useSmartBack('/admin/dashboard');
  const [appointments, setAppointments] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table');
  const [filters, setFilters] = useState({
    status: '',
    service: '',
    cleaner: searchParams.get('cleaner_id') || '',
    search: '',
    dateFrom: '',
    dateTo: '',
  });
  const [assignDialog, setAssignDialog] = useState({ open: false, appointmentId: null });
  const [teamDraft, setTeamDraft] = useState([]);
  const [savingTeam, setSavingTeam] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      // When a specific Guru is filtered we ask the backend to include jobs
      // where they were on the team at any point (Session 4 job-history spec).
      const apptParams = new URLSearchParams({ limit: '200' });
      if (filters.cleaner) apptParams.set('cleaner_id', filters.cleaner);
      const [apptRes, cleanerRes] = await Promise.all([
        axios.get(`${API_URL}/api/appointments?${apptParams.toString()}`, { headers }),
        axios.get(`${API_URL}/api/cleaners`, { headers }).catch(() => ({ data: [] })),
      ]);
      setAppointments(apptRes.data || []);
      setCleaners(cleanerRes.data || []);
      const uniqueServices = [...new Map((apptRes.data || []).filter(a => a.service).map(a => [a.service.id, a.service])).values()];
      setServices(uniqueServices);
    } catch (_) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [headers, filters.cleaner]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    return appointments.filter(a => {
      if (filters.status && a.status !== filters.status) return false;
      if (filters.service && a.service?.id !== filters.service) return false;
      // Cleaner filter is applied server-side (matches team membership too);
      // don't double-filter client-side or supporting-guru jobs get hidden.
      if (filters.search) {
        const s = filters.search.toLowerCase();
        const match = (a.client_name || '').toLowerCase().includes(s) ||
          (a.cleaner_name || '').toLowerCase().includes(s) ||
          (a.address?.line1 || '').toLowerCase().includes(s);
        if (!match) return false;
      }
      if (filters.dateFrom && new Date(a.start_time) < new Date(filters.dateFrom)) return false;
      if (filters.dateTo && new Date(a.start_time) > new Date(filters.dateTo + 'T23:59:59')) return false;
      return true;
    });
  }, [appointments, filters]);

  const calendarEvents = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      const d = new Date(a.start_time).toDateString();
      if (!map[d]) map[d] = [];
      map[d].push(a);
    });
    return map;
  }, [filtered]);

  const handleAssign = async () => {
    if (!assignDialog.appointmentId) return;
    setSavingTeam(true);
    try {
      await axios.put(`${API_URL}/api/admin/assignments/appointment/${assignDialog.appointmentId}`,
        { staff_profile_ids: teamDraft }, { headers });
      toast.success(teamDraft.length === 0 ? 'Team cleared' : `Team updated (${teamDraft.length} guru${teamDraft.length !== 1 ? 's' : ''})`);
      setAssignDialog({ open: false, appointmentId: null });
      setTeamDraft([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update team');
    } finally {
      setSavingTeam(false);
    }
  };

  const openAssignDialog = async (appointmentId) => {
    // Prefill draft with the current team for this appointment
    try {
      const res = await axios.get(`${API_URL}/api/admin/assignments/appointment/${appointmentId}`, { headers });
      const currentTeam = (res.data?.current_team || []).map(t => t.staff_profile_id);
      setTeamDraft(currentTeam);
    } catch (_) {
      setTeamDraft([]);
    }
    setAssignDialog({ open: true, appointmentId });
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/appointments/${id}`, { status: newStatus }, { headers });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const clearFilters = () => setFilters({ status: '', service: '', cleaner: '', search: '', dateFrom: '', dateTo: '' });

  const selectedDayEvents = calendarEvents[calendarDate.toDateString()] || [];
  const sortedSelectedDayEvents = useMemo(
    () => [...selectedDayEvents].sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
    [selectedDayEvents]
  );
  const availableCleaners = useMemo(
    () => cleaners.filter(c => c.is_available !== false),
    [cleaners]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={back} data-testid="back-to-dashboard">
                <ArrowLeft className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Dashboard</span>
              </Button>
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900">Appointments</h1>
              <Badge variant="secondary" className="text-xs">{filtered.length}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant={view === 'table' ? 'default' : 'outline'}
                onClick={() => setView('table')} className={view === 'table' ? 'bg-[#F56F05] hover:bg-[#C2410C]' : ''}
                data-testid="view-table">
                <TableIcon className="h-4 w-4" />
              </Button>
              <Button size="sm" variant={view === 'calendar' ? 'default' : 'outline'}
                onClick={() => setView('calendar')} className={view === 'calendar' ? 'bg-[#F56F05] hover:bg-[#C2410C]' : ''}
                data-testid="view-calendar">
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button size="sm" onClick={fetchData} variant="outline" data-testid="refresh-btn">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>
      <AdminSubNav section="Calendar" items={SUBNAV_SCHEDULE} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Filters */}
        <Card className="border-0 shadow-md" data-testid="filters-card">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input placeholder="Search client, guru, address..." className="pl-10"
                  value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                  data-testid="search-input" />
              </div>
              <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                <SelectTrigger className="w-full sm:w-[160px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.cleaner} onValueChange={v => setFilters(f => ({ ...f, cleaner: v === 'all' ? '' : v }))}>
                <SelectTrigger className="w-full sm:w-[160px]" data-testid="filter-cleaner"><SelectValue placeholder="Guru" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Gurus</SelectItem>
                  {cleaners.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.user_name || c.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="date" className="w-full sm:w-[150px]" value={filters.dateFrom}
                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} data-testid="filter-date-from" />
              <Input type="date" className="w-full sm:w-[150px]" value={filters.dateTo}
                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} data-testid="filter-date-to" />
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters">Clear</Button>
            </div>
          </CardContent>
        </Card>

        {view === 'table' ? (
          /* TABLE VIEW */
          <>
            {/* Desktop Table */}
            <Card className="border-0 shadow-lg overflow-hidden hidden md:block" data-testid="appointments-table">
              <div className="overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="font-bold text-zinc-700">Date/Time</TableHead>
                    <TableHead className="font-bold text-zinc-700">Client</TableHead>
                    <TableHead className="font-bold text-zinc-700">Service</TableHead>
                    <TableHead className="font-bold text-zinc-700">Address</TableHead>
                    <TableHead className="font-bold text-zinc-700">Assigned Team</TableHead>
                    <TableHead className="font-bold text-zinc-700">Status</TableHead>
                    <TableHead className="font-bold text-zinc-700">Invoice</TableHead>
                    <TableHead className="font-bold text-zinc-700 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-12 text-zinc-400">No appointments found</TableCell></TableRow>
                  ) : filtered.map(appt => (
                    <TableRow key={appt.id} className="hover:bg-zinc-50/50 cursor-pointer group" data-testid={`appt-row-${appt.id}`}>
                      <TableCell className="whitespace-nowrap">
                        <p className="font-medium text-zinc-900 text-sm">{new Date(appt.start_time).toLocaleDateString()}</p>
                        <p className="text-xs text-zinc-500">{new Date(appt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-zinc-900 text-sm">{appt.client_name || '—'}</p>
                        <p className="text-xs text-zinc-400">{appt.client_phone || ''}</p>
                      </TableCell>
                      <TableCell className="text-sm text-zinc-700">{appt.service?.name || '—'}</TableCell>
                      <TableCell className="max-w-[160px]">
                        <p className="text-sm text-zinc-700 truncate">{appt.address?.line1 || '—'}</p>
                        <p className="text-xs text-zinc-400">{appt.address ? `${appt.address.city}, ${appt.address.state}` : ''}</p>
                      </TableCell>
                      <TableCell>
                        {appt.cleaner_name ? (
                          <button onClick={() => openAssignDialog(appt.id)}
                            className="text-sm font-medium text-zinc-700 hover:text-[#F56F05] hover:underline text-left"
                            data-testid={`edit-team-btn-${appt.id}`}>
                            {appt.cleaner_name}
                          </button>
                        ) : (
                          <button onClick={() => openAssignDialog(appt.id)}
                            className="text-xs text-[#F56F05] font-semibold hover:underline" data-testid={`assign-btn-${appt.id}`}>
                            + Assign team
                          </button>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[appt.status] || 'bg-zinc-100 text-zinc-700'}`}>
                          {appt.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {appt.invoice ? (
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${INVOICE_COLORS[appt.invoice.status] || 'bg-zinc-100'}`}>
                            {appt.invoice.status}
                          </span>
                        ) : <span className="text-xs text-zinc-400">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/appointments/${appt.id}`)}
                            data-testid={`view-btn-${appt.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {!['Cancelled', 'cancelled', 'Completed', 'completed'].includes(appt.status) && (
                            <Button size="sm" variant="ghost" title="Cancel appointment"
                              onClick={async () => {
                                if (!window.confirm(`Cancel appointment for ${appt.client_name || 'this client'}?`)) return;
                                try {
                                  await axios.post(`${API_URL}/api/appointments/${appt.id}/cancel`, {}, { headers });
                                  toast.success('Appointment cancelled');
                                  fetchData();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel'); }
                              }}
                              data-testid={`cancel-btn-${appt.id}`}>
                              <XCircle className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                          <RecordActions
                            canEdit={false}
                            canArchive={false}
                            canDelete={true}
                            deleteUrl={`/api/appointments/${appt.id}`}
                            deleteConfirmTitle="Delete this appointment?"
                            deleteConfirmDescription="Permanently removes the appointment plus its non-financial rows (photos, QC items, change requests). If the appointment has paid invoices or time entries, deletion will be blocked — cancel it instead."
                            onDeleted={fetchData}
                            headers={headers}
                            testIdPrefix="appt"
                            entityId={appt.id}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3" data-testid="appointments-cards-mobile">
              {filtered.length === 0 ? (
                <Card className="border-0 shadow-sm"><CardContent className="py-10 text-center text-sm text-zinc-400">No appointments found</CardContent></Card>
              ) : filtered.map(appt => (
                <Card key={appt.id} className="border-0 shadow-sm active:shadow-md" data-testid={`appt-card-mobile-${appt.id}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-zinc-900 text-sm truncate">{appt.client_name || '—'}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">
                          {new Date(appt.start_time).toLocaleDateString()} · {new Date(appt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium border flex-shrink-0 ${STATUS_COLORS[appt.status] || 'bg-zinc-100 text-zinc-700'}`}>
                        {appt.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-[10px] uppercase text-zinc-400 font-semibold">Service</p>
                        <p className="text-zinc-700 truncate">{appt.service?.name || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-zinc-400 font-semibold">Assigned Team</p>
                        {appt.cleaner_name ? (
                          <button onClick={() => openAssignDialog(appt.id)}
                            className="text-zinc-700 truncate hover:text-[#F56F05] hover:underline text-left"
                            data-testid={`edit-team-btn-mobile-${appt.id}`}>
                            {appt.cleaner_name}
                          </button>
                        ) : (
                          <button onClick={() => openAssignDialog(appt.id)}
                            className="text-[#F56F05] font-semibold hover:underline" data-testid={`assign-btn-mobile-${appt.id}`}>
                            + Assign team
                          </button>
                        )}
                      </div>
                      <div className="col-span-2">
                        <p className="text-[10px] uppercase text-zinc-400 font-semibold">Address</p>
                        <p className="text-zinc-700 truncate">{appt.address?.line1 || '—'}{appt.address?.city ? ` · ${appt.address.city}` : ''}</p>
                      </div>
                      {appt.invoice && (
                        <div className="col-span-2">
                          <p className="text-[10px] uppercase text-zinc-400 font-semibold">Invoice</p>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${INVOICE_COLORS[appt.invoice.status] || 'bg-zinc-100'}`}>
                            {appt.invoice.status}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
                      <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => navigate(`/admin/appointments/${appt.id}`)}
                        data-testid={`view-btn-mobile-${appt.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> View
                      </Button>
                      {!['Cancelled', 'cancelled', 'Completed', 'completed'].includes(appt.status) && (
                        <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                          onClick={async () => {
                            if (!window.confirm(`Cancel appointment for ${appt.client_name || 'this client'}?`)) return;
                            try {
                              await axios.post(`${API_URL}/api/appointments/${appt.id}/cancel`, {}, { headers });
                              toast.success('Appointment cancelled');
                              fetchData();
                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel'); }
                          }}
                          data-testid={`cancel-btn-mobile-${appt.id}`}>
                          <XCircle className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <RecordActions
                        canEdit={false}
                        canArchive={false}
                        canDelete={true}
                        deleteUrl={`/api/appointments/${appt.id}`}
                        deleteConfirmTitle="Delete this appointment?"
                        deleteConfirmDescription="Permanently removes the appointment plus its non-financial rows (photos, QC items, change requests). If the appointment has paid invoices or time entries, deletion will be blocked — cancel it instead."
                        onDeleted={fetchData}
                        headers={headers}
                        testIdPrefix="appt-mobile"
                        entityId={appt.id}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) : (
          /* CALENDAR VIEW */
          <div className="grid lg:grid-cols-[340px_1fr] gap-6" data-testid="calendar-view">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-4">
                <Calendar
                  mode="single"
                  selected={calendarDate}
                  onSelect={(d) => d && setCalendarDate(d)}
                  modifiers={{
                    hasEvents: (date) => !!calendarEvents[date.toDateString()]
                  }}
                  modifiersStyles={{
                    hasEvents: { fontWeight: 'bold', color: '#F56F05' }
                  }}
                  className="rounded-md border"
                />
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardHeader className="border-b border-zinc-100 py-3">
                <CardTitle className="text-base font-bold">
                  {calendarDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  <Badge variant="secondary" className="ml-2">{selectedDayEvents.length} jobs</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {selectedDayEvents.length === 0 ? (
                  <div className="py-12 text-center text-zinc-400">
                    <CalendarIcon className="h-10 w-10 mx-auto mb-3 text-zinc-200" />
                    <p>No appointments on this day</p>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {sortedSelectedDayEvents.map(appt => (
                      <Link key={appt.id} to={`/admin/appointments/${appt.id}`}
                        className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors" data-testid={`cal-event-${appt.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="text-center min-w-[50px]">
                            <p className="text-lg font-bold text-[#F56F05]">
                              {new Date(appt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium text-zinc-900 text-sm">{appt.client_name || 'Client'}</p>
                            <p className="text-xs text-zinc-500">{appt.service?.name} {appt.cleaner_name ? `• ${appt.cleaner_name}` : ''}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[appt.status]}`}>
                          {appt.status}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Assign Team Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(o) => setAssignDialog({ ...assignDialog, open: o })}>
        <DialogContent className="sm:max-w-md" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserCog className="h-5 w-5 text-[#F56F05]" />Assigned Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-zinc-500">
              Pick every Guru working this job. The first Guru is the lead — they&apos;ll appear on the calendar and any legacy views.
            </p>
            <MultiGuruSelect
              value={teamDraft}
              onChange={setTeamDraft}
              headers={headers}
              gurusOverride={availableCleaners}
              testIdPrefix="appt-team"
              placeholder="Assign gurus…"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAssignDialog({ open: false, appointmentId: null })}>Cancel</Button>
              <Button onClick={handleAssign} disabled={savingTeam}
                className="bg-[#F56F05] hover:bg-[#C2410C] text-white" data-testid="confirm-assign">
                {savingTeam ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Save team
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAppointmentsPage;
